import { useAtomValue, useSetAtom } from "jotai";
import { atoms } from "@/state";

const SUPPORT_EMAIL = "jim@squint.ai";

// Covers everything between page paint and the app being usable: the data
// fetch after sign-in and the error path when reads fail — a 403 means the
// signed-in Microsoft account isn't on the server's allow-list.
//
// Only mounted under the authed layout, so /sign-in is never blocked.
export const LoadingOverlay: React.FC = () => {
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const isLoading = useAtomValue(atoms.data.isLoading);
  const hasError = useAtomValue(atoms.data.hasError);
  const isForbidden = useAtomValue(atoms.data.isForbidden);
  const errorMessages = useAtomValue(atoms.data.errorMessages);
  const email = useAtomValue(atoms.auth.email);
  const signOut = useSetAtom(atoms.auth.signOut);

  // Real error from the API AFTER sign-in. While signed out the data queries
  // are disabled (see DataAtoms), so this branch only triggers on a genuine
  // allowlist rejection (403) or server/schema failure.
  if (signedIn && hasError) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-50/95 px-6 backdrop-blur-sm dark:bg-gray-950/95">
        <div className="flex max-w-md flex-col gap-4 rounded-2xl border border-red-200 bg-white p-6 shadow-lg dark:border-red-900 dark:bg-gray-900">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">
            Couldn't load the product database
          </h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {isForbidden
              ? "This account isn't allowed to use PLUS. Try signing out and back in with your Nailor Microsoft account."
              : "The app couldn't load data from the PLUS server. Try signing out and back in with your Nailor Microsoft account."}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            If that doesn't fix it, contact{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=PLUS%20access%20issue`}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {SUPPORT_EMAIL}
            </a>
            {email && (
              <>
                {" "}
                and mention you signed in as <code className="font-mono">{email}</code>.
              </>
            )}
          </p>
          <button
            type="button"
            onClick={() => signOut()}
            className="self-start rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Sign out and try again
          </button>
          <details className="text-xs text-gray-500 dark:text-gray-400">
            <summary className="cursor-pointer">Technical details</summary>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {errorMessages.map((m) => (
                <li key={m} className="font-mono">
                  {m}
                </li>
              ))}
            </ul>
          </details>
        </div>
      </div>
    );
  }

  if (signedIn && isLoading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-50/95 backdrop-blur-sm dark:bg-gray-950/95">
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <span
            className="inline-block h-3 w-3 animate-pulse rounded-full bg-blue-600 dark:bg-blue-400"
            aria-hidden
          />
          Loading product database…
        </div>
      </div>
    );
  }

  return null;
};
